import { useNode } from "@craftjs/core";
import React from "react";
import {
  ColorInput,
  SelectInput,
  UnitInput,
  Panel,
  Tabs,
  Spacer,
  ToggleInput,
  TabsV2,
  BorderInput,
  RangeNumberInput,
  PaddingInput,
  IconInput,
  NumberInput,
} from "../../input";
import { supportLibrary } from "../../block-supports";
import { changeTransition, isUpdatedFromDefault } from "../../utils";
import { INPUT_LIMITS } from "../../input-limits";

export const TableOfContentsSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    dataSrc = "cms-post",
    activeReactive,
    scrollOffset,
    headings = {
      h2: true,
    },
    typography,
    typographyActive,
    typographyHover,
    color,
    colorActive,
    colorHover,
    iconColor,
    iconColorActive,
    iconColorHover,
    iconSize,
    border,
    borderActive,
    borderHover,
    transition,
    padding,
    paddingActive,
    paddingHover,
    gap,
    icon,
    customIcon,
    hasCustomIcon,
    hasListIcon,
    truncate,
  } = props;

  function changeTypography(prop, key, val) {
    setProp((props) => {
      props[prop] = {
        ...(props[prop] ? props[prop] : {}),
        [key]: val,
      };
    });
  }

  function RenderTypography({ propKey, prop }) {
    return (
      <>
        <UnitInput
          resetValue={undefined}
          value={prop?.size}
          onChange={(val) => {
            changeTypography(propKey, "size", val);
          }}
          label={"Size"}
        />
        <UnitInput
          resetValue={undefined}
          value={prop?.lineHeight}
          onChange={(val) => {
            changeTypography(propKey, "lineHeight", val);
          }}
          label={"Line Height"}
        />
        <UnitInput
          resetValue={undefined}
          value={prop?.letterSpacing}
          onChange={(val) => {
            changeTypography(propKey, "letterSpacing", val);
          }}
          label={"Letter Spacing"}
        />
        <UnitInput
          resetValue={undefined}
          value={prop?.wordSpacing}
          onChange={(val) => {
            changeTypography(propKey, "wordSpacing", val);
          }}
          label={"Word Spacing"}
        />
        <SelectInput
          resetValue={undefined}
          value={prop?.weight}
          onChange={(val) => {
            changeTypography(propKey, "weight", val);
          }}
          options={[
            { label: "100", value: "100" },
            { label: "200", value: "200" },
            { label: "300", value: "300" },
            { label: "400", value: "400" },
            { label: "500", value: "500" },
            { label: "600", value: "600" },
            { label: "700", value: "700" },
            { label: "800", value: "800" },
            { label: "900", value: "900" },
          ]}
          label="Weight"
        />
        <SelectInput
          resetValue={undefined}
          value={prop?.transform}
          onChange={(val) => {
            changeTypography(propKey, "transform", val);
          }}
          options={[
            { label: "None", value: "" },
            { label: "Uppercase", value: "uppercase" },
            { label: "Lowercase", value: "lowercase" },
            { label: "Capitalize", value: "capitalize" },
          ]}
          label="Transform"
        />
        <SelectInput
          resetValue={undefined}
          value={prop?.decoration}
          onChange={(val) => {
            changeTypography(propKey, "decoration", val);
          }}
          options={[
            { label: "None", value: "" },
            { label: "Underline", value: "underline" },
            { label: "Overline", value: "overline" },
          ]}
          label="Decoration"
        />
        <SelectInput
          resetValue={undefined}
          value={prop?.style}
          onChange={(val) => {
            changeTypography(propKey, "style", val);
          }}
          options={[
            {
              label: "Normal",
              value: "normal",
            },
            {
              label: "Italic",
              value: "italic",
            },
          ]}
          label="Style"
        />
      </>
    );
  }

  const typographyTabs = [
    {
      label: "Initial",
      key: "initial",
      children: RenderTypography({
        propKey: "typography",
        prop: typography,
      }),
    },
    {
      label: "Active",
      key: "active",
      children: RenderTypography({
        propKey: "typographyActive",
        prop: typographyActive,
      }),
    },
    {
      label: "Hover",
      key: "hover",
      children: RenderTypography({
        propKey: "typographyHover",
        prop: typographyHover,
      }),
    },
  ];

  function GeneralSettings() {
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing="1rem">
          <SelectInput
            isResetVisible={false}
            label="Data Source"
            value={dataSrc}
            options={[
              {
                label: "Whole Page",
                value: "page",
              },
              {
                label: "Current CMS Post",
                value: "cms-post",
              },
            ]}
            onChange={(val) => {
              setProp((props) => (props.dataSrc = val));
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="On Scroll Active?"
            value={activeReactive}
            onChange={(val) => {
              setProp((props) => {
                props.activeReactive = val;
              });
            }}
          />
          <ToggleInput
            label="Truncate Text"
            value={truncate}
            onChange={(val) => {
              setProp((props) => {
                props.truncate = val;
              });
            }}
          />
          <div className="text-xs mb-2">Include Headings</div>
          <ToggleInput
            isResetVisible={false}
            label="H2"
            value={headings.h2}
            onChange={(val) => {
              setProp((props) => {
                props.headings = {
                  ...(headings ? headings : {}),
                  h2: val,
                };
              });
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="H3"
            value={headings.h3}
            onChange={(val) => {
              setProp((props) => {
                props.headings = {
                  ...(headings ? headings : {}),
                  h3: val,
                };
              });
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="H4"
            value={headings.h4}
            onChange={(val) => {
              setProp((props) => {
                props.headings = {
                  ...(headings ? headings : {}),
                  h4: val,
                };
              });
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="H5"
            value={headings.h5}
            onChange={(val) => {
              setProp((props) => {
                props.headings = {
                  ...(headings ? headings : {}),
                  h5: val,
                };
              });
            }}
          />
          <ToggleInput
            isResetVisible={false}
            label="H6"
            value={headings.h6}
            onChange={(val) => {
              setProp((props) => {
                props.headings = {
                  ...(headings ? headings : {}),
                  h6: val,
                };
              });
            }}
          />
          <UnitInput
            isResetVisible={false}
            label="Gap"
            value={gap}
            onChange={(val) => {
              setProp((props) => {
                props.gap = val;
              });
            }}
          />
          <NumberInput
            // isResetVisible={false}
            label="Scroll Offset (px)"
            value={scrollOffset}
            onChange={(val) => {
              setProp((props) => {
                props.scrollOffset = val;
              });
            }}
            // min={-1000}
            // max={1000}
            // step={1}

            min={INPUT_LIMITS["toc-scroll-offset"].min}
            max={INPUT_LIMITS["toc-scroll-offset"].max}
            step={INPUT_LIMITS["toc-scroll-offset"].step}
          />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([{ key: typography }])}
          id={`block-setting-${id}-typography`}
          title={"Typography"}
          initialOpen={false}
        >
          <TabsV2 items={typographyTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: hasListIcon },
            { key: icon },
            { key: hasCustomIcon },
            { key: customIcon },
            { key: iconSize },
          ])}
          id={`block-setting-${id}-icon`}
          title={"Icon"}
          initialOpen={false}
        >
          <ToggleInput
            label="List Icon"
            value={hasListIcon}
            onChange={(val) => {
              setProp((props) => {
                props.hasListIcon = val;
              });
            }}
          />
          {hasListIcon && (
            <>
              <IconInput
                resetValue={undefined}
                activeIcon={icon}
                setActiveIcon={(val) => {
                  setProp((props) => {
                    props.icon = val;
                  });
                }}
                supportCustom
                hasCustomIcon={hasCustomIcon}
                setHasCustomIcon={(val) => {
                  setProp((props) => {
                    props.hasCustomIcon = val;
                  });
                }}
                customIcon={customIcon}
                setCustomIcon={(val) => {
                  setProp((props) => {
                    props.customIcon = val;
                  });
                }}
              />
              <UnitInput
                isResetVisible={false}
                label="Icon Size"
                value={iconSize}
                onChange={(val) => {
                  setProp((props) => {
                    props.iconSize = val;
                  });
                }}
              />
            </>
          )}
        </Panel>
      </div>
    );
  }

  function StyleSettings() {
    const colorTabs = [
      {
        label: "Initial",
        key: "initial",
        children: handleColorStates({
          colorPropKey: "color",
          colorProp: color,
          iconColorPropKey: "iconColor",
          iconColorProp: iconColor,
        }),
      },
      {
        label: "Active",
        key: "active",
        children: handleColorStates({
          colorPropKey: "colorActive",
          colorProp: colorActive,
          iconColorPropKey: "iconColorActive",
          iconColorProp: iconColorActive,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleColorStates({
          colorPropKey: "colorHover",
          colorProp: colorHover,
          iconColorPropKey: "iconColorHover",
          iconColorProp: iconColorHover,
        }),
      },
    ];

    function handleColorStates({
      colorPropKey,
      colorProp,
      iconColorPropKey,
      iconColorProp,
    }) {
      return (
        <>
          <ColorInput
            resetValue={undefined}
            resetValueTablet={undefined}
            resetValueMobile={undefined}
            value={colorProp}
            onChange={(val) => {
              setProp((props) => {
                props[colorPropKey] = val;
              });
            }}
            label={"Color"}
          />
          {hasListIcon && (
            <ColorInput
              resetValue={undefined}
              resetValueTablet={undefined}
              resetValueMobile={undefined}
              value={iconColorProp}
              onChange={(val) => {
                setProp((props) => {
                  props[iconColorPropKey] = val;
                });
              }}
              label={"Icon Color"}
            />
          )}
          {(colorPropKey === "colorHover" ||
            iconColorPropKey === "iconColorHover") && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.["color"]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["color"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }

    return (
      <Spacer spacing={"1rem"}>
        <TabsV2 items={colorTabs} />
      </Spacer>
    );
  }

  function AdvancedSettings() {
    const paddingTabs = [
      {
        label: "Initial",
        key: "initial",
        children: handleLayoutStates({
          borderPropKey: "border",
          borderProp: border,
          paddingPropKey: "padding",
          paddingProp: padding,
        }),
      },
      {
        label: "Active",
        key: "active",
        children: handleLayoutStates({
          borderPropKey: "borderActive",
          borderProp: borderActive,
          paddingPropKey: "paddingActive",
          paddingProp: paddingActive,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleLayoutStates({
          borderPropKey: "borderHover",
          borderProp: borderHover,
          paddingPropKey: "paddingHover",
          paddingProp: paddingHover,
        }),
      },
    ];

    function handleLayoutStates({
      borderPropKey,
      borderProp,
      paddingPropKey,
      paddingProp,
    }) {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={borderProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderPropKey] = val;
              });
            }}
            label={"Border"}
          />
          <PaddingInput
            resetValue={undefined}
            value={paddingProp}
            onChange={(val) => {
              setProp((props) => {
                props[paddingPropKey] = val;
              });
            }}
            label={"Padding"}
          />

          {borderPropKey === "borderHover" ||
            (paddingPropKey === "paddingHover" && (
              <RangeNumberInput
                min={0}
                max={10}
                step={0.05}
                value={transition?.types?.[("border", "padding")]?.duration}
                onChange={(val) => {
                  changeTransition(
                    ["border", "padding"],
                    { duration: val },
                    transition,
                    setProp,
                  );
                }}
                label={"Transition Time"}
              />
            ))}
        </>
      );
    }
    const borderTabs = [
      {
        label: "Initial",
        key: "initial",
        children: handleBorderStates({
          borderPropKey: "border",
          borderProp: border,
        }),
      },
      {
        label: "Active",
        key: "active",
        children: handleBorderStates({
          borderPropKey: "borderActive",
          borderProp: borderActive,
        }),
      },
      {
        label: "Hover",
        key: "hover",
        children: handleBorderStates({
          borderPropKey: "borderHover",
          borderProp: borderHover,
        }),
      },
    ];

    function handleBorderStates({ borderPropKey, borderProp }) {
      return (
        <>
          <BorderInput
            resetValue={undefined}
            value={borderProp}
            onChange={(val) => {
              setProp((props) => {
                props[borderPropKey] = val;
              });
            }}
            label={"Border"}
          />

          {borderPropKey === "borderHover" && (
            <RangeNumberInput
              min={0}
              max={10}
              step={0.05}
              value={transition?.types?.["border"]?.duration}
              onChange={(val) => {
                changeTransition(
                  ["border"],
                  { duration: val },
                  transition,
                  setProp,
                );
              }}
              label={"Transition Time"}
            />
          )}
        </>
      );
    }
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: padding },
            { key: paddingActive },
            { key: paddingHover },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <TabsV2 items={paddingTabs} />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderActive },
            { key: borderHover },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={true}
        >
          <TabsV2 items={borderTabs} />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([]),
    },
  ];

  return (
    <div>
      <Tabs items={tabs} id={`block-settings-tab-${id}`} />
    </div>
  );
};
